<template>
  <div class="container-fluid py-4">
    <!-- Titre -->
    <div class="row mb-4">
      <div class="col-12 d-flex justify-content-between align-items-center">
        <h4 class="mb-0">{{ t('officeExpenses.title') }}</h4>
      </div>
    </div>

    <!-- Header -->
    <div class="row mb-4">
      <div class="col-12">
        <div
          class="card-header p-3 position-relative mt-n4 mx-3 z-index-2 d-flex justify-content-between align-items-center"
          style="background: linear-gradient(195deg, #4b5563, #8d2e38); border-radius: 12px; box-shadow: 0 4px 6px rgba(0,0,0,0.1);"
        >
          <h6 class="text-white text-capitalize m-0">
            {{ t('officeExpenses.dashboardTitle') }}
          </h6>
        </div>
      </div>
    </div>

    <!-- Résumé -->
    <div class="row mx-2 mb-4">
      <div
        class="col-12 col-sm-6 col-md-3 mb-3"
        v-for="(card, index) in summaryCards"
        :key="index"
      >
        <div
          class="card text-center p-3 card-summary"
          :style="{
            backgroundColor: '#ffffff',
            border: '2px solid #b03a45',
            color: '#b03a45',
            borderRadius: '12px',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
            cursor: card.onClick ? 'pointer' : 'default'
          }"
          @click="card.onClick && card.onClick()"
        >
          <div class="icon mb-2">
            <i :class="card.icon" class="icon-circle"></i>
          </div>
          <h5 class="fw-bold mb-1">{{ card.title }}</h5>
          <p class="amount mb-1">{{ card.value }}</p>
          <p v-if="card.extra" class="details mt-auto">
            {{ card.extra }}
          </p>
        </div>
      </div>
    </div>

    <!-- Liste Incomes -->
    <div v-if="activeTab === 'incomes'" class="mt-4 mx-2">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h5 class="fw-bold" style="color: #b03a45;">{{ t('officeExpenses.incomeList') }}</h5>
        <router-link to="/office-expenses/incomes/add">
          <button class="btn text-white" style="background-color: #b03a45; border-radius: 8px;">
            + {{ t('officeExpenses.addIncome') }}
          </button>
        </router-link>
      </div>

      <div class="card custom-table-card">
        <div class="card-body px-0 pb-2">
          <div class="table-responsive p-0">
            <table class="table mb-0 custom-table">
              <thead>
                <tr>
                  <th>{{ t('officeExpenses.type') }}</th>
                  <th>{{ t('officeExpenses.amount') }}</th>
                  <th>{{ t('officeExpenses.date') }}</th>
                  <th>{{ t('officeExpenses.note') }}</th>
                  <th class="text-center">{{ t('officeExpenses.actions') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="income in validIncomes" :key="income.id">
                  <td>{{ income.type }}</td>
                  <td>{{ income.amount }}</td>
                  <td>{{ income.date }}</td>
                  <td>{{ income.note }}</td>
                  <td class="text-center">
                    <router-link :to="`/office-expenses/incomes/edit/${income.id}`" class="btn btn-sm btn-outline-primary me-2">
                      ✏️
                    </router-link>
                    <button class="btn btn-sm btn-outline-danger" @click="deleteIncome(income.id)">
                      🗑️
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
            <div v-if="validIncomes.length === 0" class="text-center py-4 text-muted">
              {{ t('officeExpenses.noIncomes') }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Liste Expenses -->
    <div v-if="activeTab === 'expenses'" class="mt-4 mx-2">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h5 class="fw-bold" style="color: #b03a45;">{{ t('officeExpenses.expenseList') }}</h5>
        <router-link to="/office-expenses/expenses/add">
          <button class="btn text-white" style="background-color: #b03a45; border-radius: 8px;">
            + {{ t('officeExpenses.addExpense') }}
          </button>
        </router-link>
      </div>

      <div class="card custom-table-card">
        <div class="card-body px-0 pb-2">
          <div class="table-responsive p-0">
            <table class="table mb-0 custom-table">
              <thead>
                <tr>
                  <th>{{ t('officeExpenses.type') }}</th>
                  <th>{{ t('officeExpenses.category') }}</th>
                  <th>{{ t('officeExpenses.company') }}</th>
                  <th>{{ t('officeExpenses.amount') }}</th>
                  <th>{{ t('officeExpenses.date') }}</th>
                  <th>{{ t('officeExpenses.note') }}</th>
                  <th class="text-center">{{ t('officeExpenses.actions') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="expense in validExpenses" :key="expense.id">
                  <td>{{ expense.type }}</td>
                  <td>{{ expense.category_expense?.category_name || 'N/A' }}</td>
                  <td>{{ getCompanyName(expense.company_id) }}</td>
                  <td>{{ expense.amount }}</td>
                  <td>{{ expense.date }}</td>
                  <td>{{ expense.note }}</td>
                  <td class="text-center">
                    <router-link :to="`/office-expenses/expenses/edit/${expense.id}`" class="btn btn-sm btn-outline-primary me-2">
                      ✏️
                    </router-link>
                    <button class="btn btn-sm btn-outline-danger" @click="deleteExpense(expense.id)">
                      🗑️
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
            <div v-if="validExpenses.length === 0" class="text-center py-4 text-muted">
              {{ t('officeExpenses.noExpenses') }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue'
import axios from '@/api'
import { useToast } from 'vue-toastification'
import { useI18n } from 'vue-i18n'
import DailySummaryService from '@/services/dailySummaryService'

const { t } = useI18n()
const toast = useToast()

const summary = ref({
  expenses: 0,
  incomes: 0,
  balance: 0,
  budget: 0,
})

const incomes = ref([])
const expenses = ref([])
const activeTab = ref('')
const companies = ref([])

const getCompanyName = (id) => {
  if (!id) return 'N/A'
  const company = companies.value.find(c => Number(c.id) === Number(id))
  return company ? company.company_name : 'N/A'
}

const fetchSummary = async () => {
  try {
    const res = await DailySummaryService.getSummary()
    const data = res.data
    summary.value = {
      expenses: parseFloat(data.expense ?? 0),
      incomes: parseFloat(data.income ?? 0),
      budget: parseFloat(data.initial_budget ?? 0),
      balance: parseFloat(data.current_balance ?? 0),
    }
  } catch {
    toast.error(t('officeExpenses.errors.loadSummary'))
  }
}

const fetchCompanies = async () => {
  try {
    const res = await axios.get('/companies')
    companies.value = res.data || []
  } catch {
    toast.error(t('officeExpenses.errors.loadCompanies'))
  }
}

const fetchIncomes = async () => {
  try {
    const res = await axios.get('/Financial/incomes')
    incomes.value = Array.isArray(res.data.data.data) ? res.data.data.data : []
  } catch {
    toast.error(t('officeExpenses.errors.loadIncomes'))
  }
}

const fetchExpenses = async () => {
  try {
    const res = await axios.get('/Financial/expenses')
    expenses.value = Array.isArray(res.data.data) ? res.data.data : []
  } catch {
    toast.error(t('officeExpenses.errors.loadExpenses'))
  }
}

const deleteExpense = async (id) => {
  if (!confirm(t('officeExpenses.confirm.deleteExpense'))) return
  try {
    await axios.delete(`/Financial/expenses/${id}`)
    toast.success(t('officeExpenses.messages.expenseDeleted'))
    await fetchExpenses()
    await fetchSummary()
  } catch {
    toast.error(t('officeExpenses.errors.deleteExpense'))
  }
}

const deleteIncome = async (id) => {
  if (!confirm(t('officeExpenses.confirm.deleteIncome'))) return
  try {
    await axios.delete(`/Financial/incomes/${id}`)
    toast.success(t('officeExpenses.messages.incomeDeleted'))
    await fetchIncomes()
    await fetchSummary()
  } catch {
    toast.error(t('officeExpenses.errors.deleteIncome'))
  }
}

onMounted(() => {
  fetchSummary()
  fetchIncomes()
  fetchExpenses()
  fetchCompanies()
})

watch(activeTab, (tab) => {
  if (tab === 'incomes') fetchIncomes()
  if (tab === 'expenses') fetchExpenses()
})

const validIncomes = computed(() => incomes.value.filter(i => i && i.id))
const validExpenses = computed(() => expenses.value.filter(e => e && e.id))

const giftExpensesTotal = computed(() =>
  validExpenses.value.filter(e => e.type === 'gift')
    .reduce((sum, e) => sum + parseFloat(e.amount ?? 0), 0)
)

const cashExpensesTotal = computed(() =>
  validExpenses.value.filter(e => e.type === 'cash')
    .reduce((sum, e) => sum + parseFloat(e.amount ?? 0), 0)
)

const giftIncomesTotal = computed(() =>
  validIncomes.value.filter(i => i.type === 'gift')
    .reduce((sum, i) => sum + parseFloat(i.amount ?? 0), 0)
)

const cashIncomesTotal = computed(() =>
  validIncomes.value.filter(i => i.type === 'cash')
    .reduce((sum, i) => sum + parseFloat(i.amount ?? 0), 0)
)

const summaryCards = computed(() => [
  {
    title: t('officeExpenses.cards.expenses'),
    value: `${summary.value.expenses.toFixed(3)} Dinars`,
    icon: 'fas fa-wallet',
    onClick: () => (activeTab.value = 'expenses'),
    extra: `🎁 ${giftExpensesTotal.value.toFixed(3)} | 💵 ${cashExpensesTotal.value.toFixed(3)}`
  },
  {
    title: t('officeExpenses.cards.incomes'),
    value: `${summary.value.incomes.toFixed(3)} Dinars`,
    icon: 'fas fa-coins',
    onClick: () => (activeTab.value = 'incomes'),
    extra: `🎁 ${giftIncomesTotal.value.toFixed(3)} | 💵 ${cashIncomesTotal.value.toFixed(3)}`
  },
  {
    title: t('officeExpenses.cards.balance'),
    value: `${summary.value.balance.toFixed(3)} Dinars`,
    icon: 'fas fa-balance-scale'
  },
  {
    title: t('officeExpenses.cards.budget'),
    value: `${summary.value.budget.toFixed(3)} Dinars`,
    icon: 'fas fa-chart-pie'
  },
])
</script>

<style scoped>
.card-summary {
  min-height: 190px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  text-align: center;
}
.card-summary .icon-circle {
  font-size: 20px;
  padding: 12px;
  background-color: #b03a45;
  color: white;
  border-radius: 50%;
}
.card-summary .amount { font-weight: 500; font-size: 1rem; }
.card-summary .details { font-size: 0.875rem; color: #b03a45; }
.custom-table-card { border-radius: 12px; box-shadow: 0 0 8px rgba(0, 0, 0, 0.1); }
.custom-table thead tr { background-color: #f0f0f0; color: #333; font-weight: bold; }
.custom-table tbody tr:hover { background-color: #f9f9f9; }
.custom-table th, .custom-table td { padding: 12px 16px; vertical-align: middle; font-size: 0.95rem; }
</style>
